import * as React from 'react';
import Axios from 'src/services/axios';
import { isEmpty, forEach, isArray, isNil } from 'lodash';
import CardActions from '@material-ui/core/CardActions';
import { ClientDataApi } from 'src/services/configuration/codecs/confdefnView';
import { styles, MUIStyles } from './LifecycleParametersEditor.styles';
import Lifecycle from 'src/pages/AssortmentBuild/LinePlan/Lifecycle/Lifecycle';
import StoreEligibility, { StoreData } from 'src/pages/AssortmentBuild/LinePlan/StoreEligibility/StoreEligibility';
import AcceptButton from 'src/components/AcceptButton/AcceptButton';
import RejectButton from 'src/components/RejectButton/RejectButton';

import { ICellEditorParams } from '@ag-grid-community/core';
import { SPECIAL_STORE_GROUP } from 'src/utils/Domain/Constants';
import { classes } from 'typestyle';
import {
  MultiRangeEditors,
  StyleEditConfigColumn,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.types';
import { LifecycleData } from 'src/components/LifecycleStoreModal/LifecycleStoreModal';
import { Overlay } from 'src/common-ui/index';
import { maybeReturnNestData } from 'src/utils/Http/NestedDatas';
import { MuiThemeProvider } from '@material-ui/core';
import { muiTheme } from 'src/utils/Style/Theme';
import { updateRangingParams } from '../StyleEditSection.client';

interface DependentsData {
  [key: string]: string[];
}

// tslint:disable: no-any
type PlanningColumn = any;
type WeekRangeColumn = any;
// tslint:enable: no-any

interface LifecycleConfig {
  text: string;
  multiRangeEditors: MultiRangeEditors;
  planningColumns: PlanningColumn[];
  weekRangeColumns: WeekRangeColumn[];
}

export interface LifecycleParametersProps extends ICellEditorParams {
  dataApiLifecycle: ClientDataApi;
  dataApiStore: ClientDataApi;
  lifecycleConfig: ClientDataApi;
  storeConfig: ClientDataApi;
  dependentsApi?: ClientDataApi;
  floorset: string;
  product: string;
  headerSubtext: string;
  tabIndex?: number;
}

export interface LifecycleParametersState {
  activeTabIndex: number;
  lifecycleConfig: LifecycleConfig | null;
  lifecycleData: LifecycleData | null;
  savedLifecycleData: LifecycleData | null;
  storeData: StoreData | null;
  storeConfig: StyleEditConfigColumn[];
  dependentsData: DependentsData;
  saved: boolean;
  overlayWidth: number;
  overlayHeight: number;
  loading: boolean;
}

class LifecycleParametersEditor extends React.Component<LifecycleParametersProps, LifecycleParametersState> {
  lifecycleRef: React.RefObject<Lifecycle> = React.createRef();
  containerRef: React.RefObject<HTMLDivElement> = React.createRef();
  storeRef: React.RefObject<StoreEligibility> = React.createRef();
  constructor(props: LifecycleParametersProps) {
    super(props);
    let findIndex = props.tabIndex || 0;
    const originalParentColumn = this.props.column.getOriginalParent();
    if (findIndex == 0 && !isNil(originalParentColumn)) {
      const findHeader = originalParentColumn.getColGroupDef()?.headerName;
      findIndex = findHeader === 'Store Ranging' ? 1 : 0;
    }
    this.state = {
      lifecycleConfig: null,
      activeTabIndex: findIndex,
      lifecycleData: null,
      savedLifecycleData: null,
      storeData: [],
      storeConfig: [],
      dependentsData: {},
      saved: false,
      overlayWidth: window.innerWidth,
      overlayHeight: window.innerHeight,
      loading: true,
    };

    window.addEventListener('resize', this.resizeOverlay.bind(this));
  }

  resizeOverlay() {
    this.setState({
      overlayWidth: window.innerWidth,
      overlayHeight: window.innerHeight,
    });
  }

  fetchLifecycleData = async () => {
    const { dataApiLifecycle } = this.props;
    const lifecycleData = (
      await Axios.get(dataApiLifecycle.url, {
        params: {
          ...dataApiLifecycle.params,
        },
      })
    ).data.data;
    this.setState({
      lifecycleData,
    });
  };

  componentDidMount() {
    const { dataApiLifecycle, dataApiStore, lifecycleConfig, storeConfig, dependentsApi } = this.props;

    const lifecycleConfigGet = Axios.get(lifecycleConfig.url, {
      params: { ...lifecycleConfig.params },
    });
    const lifecycleData = Axios.get(dataApiLifecycle.url, {
      params: {
        ...dataApiLifecycle.params,
      },
    });
    const storeData = Axios.get(dataApiStore.url, {
      params: {
        ...dataApiStore.params,
      },
    });
    const storeConfigGet = Axios.get(storeConfig.url, {
      params: { ...storeConfig.params },
    });
    const dependentsData = Axios.get(dependentsApi && dependentsApi.url ? dependentsApi.url : '');

    const reqs = [lifecycleConfigGet, lifecycleData, storeData, dependentsData, storeConfigGet];

    Promise.all(reqs).then((responses) => {
      const respData = responses[2].data.data;
      let storeDataAsList = respData;
      if (!isArray(respData)) {
        storeDataAsList = Object.values(respData);
      }
      // fix ssg data if necessary, replace 'empty' [""] with real empty []
      forEach(storeDataAsList, (value) => {
        forEach(value, (data) => {
          const ssgValue = data[SPECIAL_STORE_GROUP];
          if (isArray(ssgValue) && !isEmpty(ssgValue) && ssgValue[0] === '') {
            data[SPECIAL_STORE_GROUP] = [];
          }
        });
      });

      this.setState({
        lifecycleConfig: maybeReturnNestData(responses[0].data) as LifecycleConfig,
        lifecycleData: responses[1].data.data,
        storeData: storeDataAsList,
        dependentsData: responses[3].data.data,
        storeConfig: responses[4].data.data,
        loading: false,
      });
    });
  }

  onClickTab = async (index: number) => {
    const { dataApiStore } = this.props;
    const { activeTabIndex } = this.state;

    if (index !== activeTabIndex) {
      this.setState({
        loading: true,
      });

      if (index === 1) {
        // case for grid actually loaded into screen
        if (
          this.lifecycleRef.current != null &&
          this.lifecycleRef.current.getLifecyclePostPromise != null &&
          this.lifecycleRef.current.gridApi != null
        ) {
          this.lifecycleRef.current.getLifecyclePostPromise().then(() => {
            Axios.get(dataApiStore.url, {
              params: {
                ...dataApiStore.params,
              },
            }).then((resp2) => {
              const storeData = resp2.data.data;
              let storeDataAsList = storeData;
              if (!isArray(storeData)) {
                storeDataAsList = Object.values(storeData);
              }

              this.setState({
                activeTabIndex: index,
                storeData: storeDataAsList,
                loading: false,
              });
            });
          });
        } else {
          // If grid not actually loaded, no way to edit data, just pass through.
          this.setState({
            activeTabIndex: index,
            loading: false,
          });
        }
      } else if (index === 0) {
        if (
          this.storeRef.current != null &&
          this.storeRef.current.saveAfterEdit != null &&
          this.storeRef.current.gridApi != null
        ) {
          const updatedAttrs = this.storeRef.current.saveAfterEdit();
          await updateRangingParams(this.props.product, updatedAttrs);
        }
        await this.fetchLifecycleData();
        this.setState({
          activeTabIndex: index,
          loading: false,
        });
      }
    }
  };

  isPopup = () => {
    return true;
  };

  getValue = () => {
    window.removeEventListener('resize', this.resizeOverlay);
    return this.props.value;
  };

  getDataStore = (storeData: any) => {
    this.setState({
      storeData: storeData as StoreData,
    });
  };

  submit = () => {
    // TECHDEBT: Added single cycle wait to submit. This allows active field blur events to fire.
    setTimeout(async () => {
      this.setState({
        loading: true,
      });

      // Save lifecycle
      if (
        this.state.activeTabIndex === 0 &&
        !isNil(this.lifecycleRef.current) &&
        this.lifecycleRef.current.postLifecycleData
      ) {
        await this.lifecycleRef.current.postLifecycleData();

        // store lifecycle data before component is unmounted
        this.setState({
          savedLifecycleData: this.lifecycleRef.current.getLifecycleData(),
        });
      }

      // Save store data
      if (this.storeRef.current) {
        const updatedAttrs = this.storeRef.current.saveAfterEdit();
        await updateRangingParams(this.props.product, updatedAttrs);
      }

      this.setState(
        {
          saved: true,
        },
        () => {
          this.props.stopEditing();
        }
      );
    });
  };

  cancel = () => {
    this.props.stopEditing();
  };

  render() {
    const { dataApiLifecycle, floorset, product } = this.props;
    const {
      activeTabIndex,
      lifecycleConfig,
      lifecycleData,
      storeData,
      dependentsData,
      storeConfig,
      loading,
    } = this.state;
    let content = <div />;
    if (lifecycleData && !isEmpty(lifecycleData)) {
      content = activeTabIndex ? (
        <StoreEligibility
          data={storeData}
          config={storeConfig}
          floorset={floorset}
          getDataStore={this.getDataStore}
          product={product}
          ref={this.storeRef}
          popoverParent={this.containerRef.current}
        />
      ) : (
        <Lifecycle
          weekRangeData={lifecycleData}
          weekRangeConfig={!isNil(lifecycleConfig) ? lifecycleConfig.weekRangeColumns : null}
          weekRangeFieldEditConfig={!isNil(lifecycleConfig) ? lifecycleConfig.multiRangeEditors : null}
          planningConfig={!isNil(lifecycleConfig) ? lifecycleConfig.planningColumns : null}
          dependentsData={dependentsData}
          product={
            dataApiLifecycle.params && dataApiLifecycle.params.product ? dataApiLifecycle.params.product : undefined
          }
          ref={this.lifecycleRef}
          popoverParent={this.containerRef.current}
        />
      );
    }

    return (
      <MuiThemeProvider theme={muiTheme}>
        {/* theme provider re-exported here because for reasons that are unclear, the theme doesn't apply witin this component correctly */}
        <div className={styles.overlay} style={{ width: this.state.overlayWidth, height: this.state.overlayHeight }}>
          <div data-qa="ag-popover" className={classes(styles.modalContainer, 'ag-theme-material')}>
            <div className={styles.cardHeader}>Style Color Parameters</div>
            {loading && <Overlay type="loading" visible={true} fitParent={true} />}
            <React.Fragment>
              <div className={styles.container} ref={this.containerRef}>
                <div className={styles.tabContainer}>
                  {['Lifecycle and Planning', 'Store Ranging'].map((name, index) => {
                    const tabClass = activeTabIndex === index ? styles.tabButtonActive : styles.tabButton;
                    return (
                      <button className={tabClass} onClick={() => this.onClickTab(index)} key={name}>
                        {name}
                      </button>
                    );
                  })}
                </div>
                <div className={styles.receiptContainer}>
                  <div className={styles.subheaderTextContainer}>
                    <span>{this.props.headerSubtext}</span>
                  </div>
                  <div className={styles.contentContainer}>{content}</div>
                </div>
              </div>
              <div className={styles.actionsContainer}>
                <CardActions classes={MUIStyles.cardActions}>
                  <AcceptButton onClick={() => this.submit()} />
                  <RejectButton onClick={this.cancel} />
                </CardActions>
              </div>
            </React.Fragment>
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}

export default LifecycleParametersEditor;
